import { type UseMutationResult } from '@tanstack/react-query'
import { ArrowRightIcon } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { type StateFormData as GetStateFormData } from 'hooks/useGetStateFormData'
import { type StateFormData as PostStateFormData } from 'hooks/useStateFormMutation'
import { Banner, Button, ButtonGroup, Checkbox, useBreakpoint } from 'slab'

import LoadingSpinnerCentered from './reusable/LoadingSpinnerCentered'

type NevadaConfigForm = {
  has_pediatric_dental_coverage: boolean;
  has_no_dependent_children_under_19: boolean;
}

const NevadaForm = ({
  navigateToNextStep,
  navigateToPreviousStep,
  stateFormMutation,
  stateFormData,
  isLoading,
  error,
  refetch
}: {
  navigateToNextStep?: () => void;
  navigateToPreviousStep?: () => void;
  stateFormMutation: UseMutationResult<void, GrapeAPIErrorType, PostStateFormData, unknown>;
  stateFormData: GetStateFormData | undefined;
  isLoading: boolean;
  error: GrapeAPIErrorType | null;
  refetch: () => void;
}) => {
  const getDefaultValues = () => ({
    has_pediatric_dental_coverage: stateFormData?.state_config?.has_pediatric_dental_coverage ?? false,
    has_no_dependent_children_under_19: stateFormData?.state_config?.has_no_dependent_children_under_19 ?? false,
  })

  const { register, handleSubmit, watch, reset } = useForm<NevadaConfigForm>({
    defaultValues: getDefaultValues(),
    mode: 'onChange',
  })

  const { breakpointIsAtLeast } = useBreakpoint()

  // state form data can be slow to load, so want to make sure we update the defaults when it's ready
  useEffect(() => {
    reset(getDefaultValues())
  }, [stateFormData])

  const [submitError, setSubmitError] = useState<string | null>(null)

  const hasPediatricDentalCoverage = watch('has_pediatric_dental_coverage')
  const hasNoDependentChildrenUnder19 = watch('has_no_dependent_children_under_19')

  const canProceed = hasPediatricDentalCoverage || hasNoDependentChildrenUnder19
  const bothSelected = hasPediatricDentalCoverage && hasNoDependentChildrenUnder19

  const onFormSubmit = async (formData: NevadaConfigForm) => {
    try {
      if (!formData.has_pediatric_dental_coverage && !formData.has_no_dependent_children_under_19) {
        setSubmitError('At least one option must be selected')
        return
      }

      await stateFormMutation.mutateAsync({
        has_pediatric_dental_coverage: formData.has_pediatric_dental_coverage,
        has_no_dependent_children_under_19: formData.has_no_dependent_children_under_19,
      }, {
        onSuccess: () => {
          refetch()
          navigateToNextStep?.()
        }
      })
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'An error occurred while submitting the form')
    }
  }

  if (isLoading) {
    return <div><LoadingSpinnerCentered /></div>
  }

  if (error) {
    return (
      <div className='mb-4 p-4 bg-red-50 border border-red-200 rounded-md'>
        <Banner
          title='Something went wrong'
          type='critical'
        >
          <p>We couldn't load this page's data. Please try again later.</p>
        </Banner>
      </div>
    )
  }

  function renderButtons () {
    const backButton = (
      <Button
        variant='secondary'
        onClick={() => {
          navigateToPreviousStep?.()
        }}
      >
        Back
      </Button>
    )

    const nextButton = (
      <Button
        rightIcon={ArrowRightIcon}
        variant='primary'
        type='submit'
        disabled={stateFormMutation.isPending || !canProceed || bothSelected}
      >
        Next
      </Button>
    )

    if (breakpointIsAtLeast('xs')) {
      return (
        <ButtonGroup size='large'>
          {backButton}
          {nextButton}
        </ButtonGroup>
      )
    }

    return (
      <ButtonGroup size='large' layout='vertical'>
        {nextButton}
        {backButton}
      </ButtonGroup>
    )
  }

  return (
    <>
      <div className='mb-8'>
        <p className='mb-3 body text-gray-500'>Medical plans don't always include pediatric dental coverage (also known as pediatric dental essential health benefits).</p>
        <p className='mb-3 body text-gray-500'>The Affordable Care Act (ACA) considers pediatric dental an essential health benefit (EHB) and requires that we seek reasonable assurance from you that you and each individual on the
          policy have coverage for pediatric dental services.
        </p>
        <p className='mb-3 body text-gray-500'>You may purchase pediatric dental coverage through a health insurance marketplace-certified standalone dental plan, or you can select a medical plan that includes pediatric dental coverage.</p>
      </div>
      <form onSubmit={handleSubmit(onFormSubmit)} className='flex flex-col'>
        <span className='body-sm-bold text-gray-800'>In order for your coverage to be effective, you must attest that:</span>
        <div className='mt-2'>
          <Checkbox
            {...register('has_pediatric_dental_coverage')}
            label='I have purchased a pediatric dental product for any dependent child on this application under the age of 19 through a Health Insurance Marketplace-certified standalone dental plan or will select a dental plan on this application'
          />
          <div className='my-3'>
            <span className='body-sm-bold text-gray-800'>OR</span>
          </div>
          <Checkbox
            {...register('has_no_dependent_children_under_19')}
            label='There are no dependent children under the age of 19 included on this application.'
          />
        </div>
        {submitError && (
          <div className='my-3'>
            <Banner
              title='A problem has occurred while processing your request'
              type='critical'
            >
              <p>{submitError}</p>
            </Banner>
          </div>
        )}
        {
          bothSelected && (
            <div className='mt-3'>
              <Banner
                title='Please select only one option'
                type='critical'
              />
            </div>
          )
        }
        <div className='mt-10'>
          {renderButtons()}
        </div>
      </form>
    </>
  )
}

export default NevadaForm
